import {t} from 'i18next';
import {FC, useEffect, useState} from 'react';
import {IIdleTimer, useIdleTimer} from 'react-idle-timer';
import {useNavigate, useLocation} from 'react-router-dom';
import ArrModal from '../../components/common/arr-modal/ArrModal';
import {Box, Typography} from '@mui/material';
import {useCountdown} from '../../utils/hooks/useCountdown';
import {clearRoleImpersonationPrefill} from '../../redux/slice/roleImpersonationSlice';
import {useDispatch} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';
import './InactivityPopup.scss';
import {timeoutConfig} from '../../config/env';
import {logoutSSO} from '../../api/endpoints/authenticationApi';
import {clearLocalStorage} from '../../utils/utils';
import {setPrevPage} from '../../redux/slice/analyticsSlice';

const idlePromptGracePeriodSec = timeoutConfig.USER_IDLE_GRACEPERIOD_SEC;
const idleTimeOutSec = timeoutConfig.USER_IDLE_TIMEOUT_SEC;

const InactivityPopup: FC = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const {countdownTime, resetCountdown, startCountdown} = useCountdown(
    idlePromptGracePeriodSec,

    (c: number) => {
      if (c < 1) {
        resetCountdown();
        onLogOut();
      }
    }
  );

  const onIdle = (
    _event: Event | undefined,
    _idleTimer: IIdleTimer | undefined
  ) => {
    setModalIsOpen(true);
    startCountdown();
  };

  useIdleTimer({
    timeout: idleTimeOutSec,
    crossTab: true,
    syncTimers: 2000,

    events: [
      'mousemove',
      'keydown',
      'mousedown',
      'wheel',
      'touchstart',
      'visibilitychange',
      'focus',
    ],

    debounce: 500,

    onIdle,

    onActive: () => {
      //DO NOT close modal automatically
      if (!modalIsOpen) {
        resetCountdown();
      }
    },
  });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    return () => {
      resetCountdown();
    };
  }, []);

  const logout = () => {
    clearLocalStorage();
    sessionStorage.removeItem('presistence-search');
    dispatch(setPrevPage(location.pathname + location.search));
    dispatch({type: 'auth/logout'});
    dispatch(clearRoleImpersonationPrefill());
    queryClient.clear();
    navigate('/login');
  };

  const SSOLogout = async () => {
    await logoutSSO();
    logout();
  };

  const onLogOut = () => {
    setModalIsOpen(false);
    SSOLogout();
  };

  const stayLoggedIn = () => {
    setModalIsOpen(false);
    resetCountdown();
  };

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes} : ${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <ArrModal
      showModal={modalIsOpen}
      showClose={false}
      onClose={stayLoggedIn}
      className="inactivity-modal-action"
      dataTestId="inactivity-modal-action"
      buttons={[
        {
          id: '1',
          label: t('sessionTimeoutModule.logOut'),
          onClick: onLogOut,
          variant: 'secondary',
          dataTestId: 'session-logout',
        },
        {
          id: '2',
          label: t('sessionTimeoutModule.continueBtn'),
          onClick: stayLoggedIn,
          variant: 'primary',
          dataTestId: 'session-login',
        },
      ]}
    >
      <Box className="inactivity-modal">
        <Box className="inactivity-modal-content">
          <Typography
            className="typo-text headline3_size inactivity-description"
            data-testid={`inactivity-popup-message`}
          >
            {t('sessionTimeoutModule.message')}{' '}
          </Typography>
          <Typography
            className="typo-text display3_size countdown"
            data-testid={`inactivity-popup-countdown-time`}
          >
            {formatTime(countdownTime)}{' '}
          </Typography>
        </Box>
        <Typography
          className="typo-text body2_size inactivity-session-message"
          data-testid={`inactivity-popup-extend-session-message`}
        >
          {t('sessionTimeoutModule.extendSessionMessage')}{' '}
        </Typography>
      </Box>
    </ArrModal>
  );
};

export default InactivityPopup;
